import type {
  AppAction,
  APIError,
  AppState,
  GetState,
  ThunkAction,
  ThunkExtras,
  Dispatch,
} from '../../types/app.types';
import type { V2Response } from '../../types/api-v2.types';
import { createSelector } from 'reselect';
import type { V2Pharmacy } from '../../types/api-v2.types';
import { updateMagicPharmacy } from './pharmacy-module';
export type PricingTierAccessDetail = {
  name: string;
  tier: string;
  pharmacyNetworkId: number;
  startedOn: string;
  endedOn: string | null | undefined;
  accessId: number;
  tierType: string;
};
export type AddNewAvailablePricingTier = {
  type: 'available_pricing_tiers/ADD_NEW';
  availablePricingTier: PricingTierAccessDetail;
};
export type AvailablePricingTiersState = Array<PricingTierAccessDetail>;
export type AvailablePricingTiersAction = AddNewAvailablePricingTier;
export type RequestMagicTierAccessResponse = {
  availablePricingTier: PricingTierAccessDetail;
  magicPharmacy: V2Pharmacy;
};
export const addNewAvailablePricingTier = (
  availablePricingTier: PricingTierAccessDetail
): AddNewAvailablePricingTier => ({
  type: 'available_pricing_tiers/ADD_NEW',
  availablePricingTier,
});
export const getUserMagicTier = (state: AppState): PricingTierAccessDetail | null | undefined =>
  state.availablePricingTiers && state.availablePricingTiers.find((pricingTier) => pricingTier.tierType === 'magic');
export const getHasMagicAccess = createSelector(getUserMagicTier, (magicTierAccess) => Boolean(magicTierAccess));
export function requestMagicTierAccess(matchCode: string): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, extras: ThunkExtras): Promise<any> {
    return extras.blinkAPIClient
      .post('/v2/user/account/magic_card/access', {
        matchCode,
      })
      .then((res) => res.data)
      .then((data: V2Response<RequestMagicTierAccessResponse>) => data.result)
      .then(async (data) => {
        await dispatch(addNewAvailablePricingTier(data.availablePricingTier));
        await dispatch(updateMagicPharmacy(data.magicPharmacy));
        return data;
      })
      .catch((err: APIError) => {
        // Don't want to throw error if user already has access after login.
        if (err.status === 409) {
          console.error(err.message);
          return;
        }

        throw err;
      });
  };
}
export function requestMagicTierAccessByPharmacy(pharmacyId: string): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, extras: ThunkExtras): Promise<any> {
    return extras.blinkAPIClient
      .post('/v2/user/account/magic_card/access_by_pharmacy', {
        pharmacyId,
      })
      .then((res) => res.data)
      .then((data: V2Response<RequestMagicTierAccessResponse>) => data.result)
      .then(async (data) => {
        await dispatch(addNewAvailablePricingTier(data.availablePricingTier));
        await dispatch(updateMagicPharmacy(data.magicPharmacy));
        return data;
      })
      .catch((err: APIError) => {
        throw err;
      });
  };
}
const initialState: AvailablePricingTiersState = [];
export default function availablePricingTiersReducer(
  state: AvailablePricingTiersState = initialState,
  action: AppAction
): AvailablePricingTiersState {
  switch (action.type) {
    case 'account/GET_SUMMARY_SUCCESS':
      return action.data.availablePricingTiers;

    case 'available_pricing_tiers/ADD_NEW':
      return [...state, action.availablePricingTier];

    default:
      return state;
  }
}
